import React, { useEffect, useState, useRef, useMemo } from "react";
import { PortalService } from "services/portal";
import Chart from "chart.js/auto";
import {
  TrendingUp, TrendingDown, ShoppingCart, Receipt, Wallet, Scale,
} from "lucide-react";

const CARD = { background: "var(--bg-card)", borderRadius: 14, border: "1px solid var(--border)" };
const MONTHS_SHORT = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

const COLORS = {
  compras: "#6366f1",
  comprasSoft: "rgba(99,102,241,0.18)",
  ventas: "#10b981",
  ventasSoft: "rgba(16,185,129,0.18)",
};

const fmtCLP = (n) => "$" + Math.round(n || 0).toLocaleString("es-CL");
const fmtShort = (n) => {
  const v = Math.abs(n || 0);
  if (v >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(n / 1e3).toFixed(0)}K`;
  return String(n || 0);
};

function KpiCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <div style={{ ...CARD, padding: 18, position: "relative", overflow: "hidden" }}>
      <div style={{
        position: "absolute", top: 0, left: 0, height: 3, width: "100%",
        background: accent || "#0353a4",
      }} />
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        background: (accent || "#0353a4") + "1a",
        display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12,
      }}>
        <Icon size={18} color={accent || "#0353a4"} />
      </div>
      <p style={{ fontSize: 11, color: "var(--text-secondary)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>{label}</p>
      <p style={{ fontSize: 22, fontWeight: 700, color: "var(--text-primary)", lineHeight: 1, marginBottom: 6, fontVariantNumeric: "tabular-nums" }}>{value}</p>
      {sub && <p style={{ fontSize: 12, color: "var(--text-muted)" }}>{sub}</p>}
    </div>
  );
}

export function PortalConsolidadoHistorico() {
  const now = new Date();
  const currentYear = now.getFullYear();
  const [fromYear, setFromYear] = useState(currentYear - 2);
  const [toYear, setToYear] = useState(currentYear);
  const [showCompras, setShowCompras] = useState(true);
  const [showVentas, setShowVentas] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const yearChartRef = useRef(null);
  const yearChartInst = useRef(null);

  useEffect(() => {
    setLoading(true);
    PortalService.getHistorico({ fromYear, toYear })
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [fromYear, toYear]);

  const applyPreset = (years) => {
    setFromYear(currentYear - (years - 1));
    setToYear(currentYear);
  };

  // Línea: tendencia compras/ventas a lo largo del rango.
  useEffect(() => {
    if (!chartRef.current) return;
    if (chartInstance.current) chartInstance.current.destroy();

    const series = data?.series || [];
    const labels = series.map((s) => `${MONTHS_SHORT[s.month - 1]} '${String(s.year).slice(-2)}`);
    const compras = series.map((s) => s.compras?.totalMonto || 0);
    const ventas = series.map((s) => s.ventas?.totalMonto || 0);

    const datasets = [];
    if (showCompras) datasets.push({
      label: "Compras", data: compras,
      borderColor: COLORS.compras, backgroundColor: COLORS.comprasSoft,
      tension: 0.4, fill: true, pointRadius: 0, pointHoverRadius: 5,
      pointBackgroundColor: "#fff", pointBorderColor: COLORS.compras, pointBorderWidth: 2,
      borderWidth: 2.5,
    });
    if (showVentas) datasets.push({
      label: "Ventas", data: ventas,
      borderColor: COLORS.ventas, backgroundColor: COLORS.ventasSoft,
      tension: 0.4, fill: true, pointRadius: 0, pointHoverRadius: 5,
      pointBackgroundColor: "#fff", pointBorderColor: COLORS.ventas, pointBorderWidth: 2,
      borderWidth: 2.5,
    });

    chartInstance.current = new Chart(chartRef.current, {
      type: "line",
      data: { labels, datasets },
      options: {
        responsive: true, maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "#0f172a", titleColor: "#fff", bodyColor: "#e2e8f0",
            padding: 12, borderRadius: 8, displayColors: true, usePointStyle: true,
            callbacks: { label: (ctx) => `${ctx.dataset.label}: ${fmtCLP(ctx.raw)}` },
          },
        },
        scales: {
          y: {
            ticks: { callback: fmtShort, color: "var(--text-muted)", font: { size: 11 } },
            grid: { color: "#f1f5f9", drawBorder: false },
            border: { display: false },
          },
          x: {
            grid: { display: false },
            ticks: { color: "var(--text-secondary)", autoSkip: true, maxTicksLimit: 12, font: { size: 11 } },
            border: { display: false },
          },
        },
      },
    });
    return () => { if (chartInstance.current) chartInstance.current.destroy(); };
  }, [data, showCompras, showVentas]);

  // Agregación anual.
  const yearlyAgg = useMemo(() => {
    const series = data?.series || [];
    const map = new Map();
    for (const s of series) {
      const cur = map.get(s.year) || { year: s.year, comprasNeto: 0, ventasNeto: 0, comprasIva: 0, ventasIva: 0, docs: 0 };
      cur.comprasNeto += s.compras?.totalNeto || 0;
      cur.ventasNeto += s.ventas?.totalNeto || 0;
      cur.comprasIva += s.compras?.totalIva || 0;
      cur.ventasIva += s.ventas?.totalIva || 0;
      cur.docs += (s.compras?.cantidadDocumentos || 0) + (s.ventas?.cantidadDocumentos || 0);
      map.set(s.year, cur);
    }
    return Array.from(map.values()).sort((a, b) => a.year - b.year);
  }, [data]);

  // Barras agrupadas por año (compras vs ventas).
  useEffect(() => {
    if (!yearChartRef.current) return;
    if (yearChartInst.current) yearChartInst.current.destroy();

    yearChartInst.current = new Chart(yearChartRef.current, {
      type: "bar",
      data: {
        labels: yearlyAgg.map((y) => String(y.year)),
        datasets: [
          { label: "Compras", data: yearlyAgg.map((y) => y.comprasNeto), backgroundColor: COLORS.compras, borderRadius: 6, borderSkipped: false, maxBarThickness: 28 },
          { label: "Ventas",  data: yearlyAgg.map((y) => y.ventasNeto),  backgroundColor: COLORS.ventas,  borderRadius: 6, borderSkipped: false, maxBarThickness: 28 },
        ],
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "#0f172a", titleColor: "#fff", bodyColor: "#e2e8f0",
            padding: 12, borderRadius: 8,
            callbacks: { label: (ctx) => `${ctx.dataset.label}: ${fmtCLP(ctx.raw)}` },
          },
        },
        scales: {
          y: { ticks: { callback: fmtShort, color: "var(--text-muted)", font: { size: 11 } }, grid: { color: "#f1f5f9", drawBorder: false }, border: { display: false } },
          x: { grid: { display: false }, ticks: { color: "var(--text-secondary)", font: { size: 11, weight: "500" } }, border: { display: false } },
        },
      },
    });
    return () => { if (yearChartInst.current) yearChartInst.current.destroy(); };
  }, [yearlyAgg]);

  const totals = data?.totals || { compras: { totalNeto: 0, totalIva: 0, cantidadDocumentos: 0 }, ventas: { totalNeto: 0, totalIva: 0, cantidadDocumentos: 0 } };
  const balance = (totals.ventas.totalNeto || 0) - (totals.compras.totalNeto || 0);
  const ivaDif = (totals.ventas.totalIva || 0) - (totals.compras.totalIva || 0);
  const monthsInRange = (toYear - fromYear + 1) * 12;

  // YoY: comparar último vs anteúltimo año agregado.
  const lastYear = yearlyAgg[yearlyAgg.length - 1];
  const prevYearAgg = yearlyAgg[yearlyAgg.length - 2];
  const yoyVentas = lastYear && prevYearAgg && prevYearAgg.ventasNeto > 0
    ? ((lastYear.ventasNeto - prevYearAgg.ventasNeto) / prevYearAgg.ventasNeto) * 100
    : null;

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 18 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: "var(--text-primary)", marginBottom: 4, letterSpacing: -0.3 }}>
          Consolidado histórico
        </h1>
        <p style={{ fontSize: 14, color: "var(--text-secondary)" }}>
          Evolución mensual y anual de tu actividad económica desde {fromYear} hasta {toYear}.
        </p>
      </div>

      {/* Range selector */}
      <div style={{ ...CARD, padding: 16, marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: 0.3 }}>Rango</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <YearStepper value={fromYear} onChange={setFromYear} min={currentYear - 6} max={toYear} />
            <span style={{ color: "var(--text-muted)", fontSize: 13 }}>→</span>
            <YearStepper value={toYear} onChange={setToYear} min={fromYear} max={currentYear} />
          </div>
          <div style={{ display: "inline-flex", background: "var(--bg-hover)", borderRadius: 999, padding: 4, gap: 2 }}>
            {[
              { y: 2, l: "2 años" },
              { y: 3, l: "3 años" },
              { y: 5, l: "5 años" },
            ].map((p) => {
              const active = toYear === currentYear && fromYear === currentYear - (p.y - 1);
              return (
                <button
                  key={p.y}
                  onClick={() => applyPreset(p.y)}
                  style={{
                    padding: "6px 14px", fontSize: 12, fontWeight: 600,
                    border: "none", borderRadius: 999, cursor: "pointer",
                    background: active ? "var(--bg-card)" : "transparent",
                    color: active ? "#0353a4" : "#64748b",
                    boxShadow: active ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                  }}
                >
                  {p.l}
                </button>
              );
            })}
          </div>

          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <ToggleChip active={showCompras} onClick={() => setShowCompras(!showCompras)} color={COLORS.compras} label="Compras" />
            <ToggleChip active={showVentas} onClick={() => setShowVentas(!showVentas)} color={COLORS.ventas} label="Ventas" />
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginBottom: 16 }}>
        <KpiCard
          icon={ShoppingCart}
          label="Compras netas"
          value={fmtCLP(totals.compras.totalNeto)}
          sub={`${totals.compras.cantidadDocumentos || 0} documentos`}
          accent={COLORS.compras}
        />
        <KpiCard
          icon={Receipt}
          label="Ventas netas"
          value={fmtCLP(totals.ventas.totalNeto)}
          sub={yoyVentas !== null ? `${yoyVentas >= 0 ? "+" : ""}${yoyVentas.toFixed(1)}% vs año anterior` : `${totals.ventas.cantidadDocumentos || 0} documentos`}
          accent={COLORS.ventas}
        />
        <KpiCard
          icon={Wallet}
          label={ivaDif >= 0 ? "IVA neto a pagar" : "Remanente IVA"}
          value={fmtCLP(Math.abs(ivaDif))}
          sub={`Acumulado del rango (${monthsInRange} meses)`}
          accent={ivaDif >= 0 ? "#f59e0b" : "#0ea5e9"}
        />
        <KpiCard
          icon={Scale}
          label="Margen del rango"
          value={fmtCLP(balance)}
          sub={balance >= 0 ? "Resultado positivo" : "Resultado negativo"}
          accent={balance >= 0 ? "#059669" : "#dc2626"}
        />
      </div>

      {/* Tendencia */}
      <div style={{ ...CARD, padding: 20, marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <h3 style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)", marginBottom: 2 }}>
              Tendencia mensual
            </h3>
            <p style={{ fontSize: 12, color: "var(--text-muted)" }}>
              Movimiento mes a mes desde {fromYear} hasta {toYear}.
            </p>
          </div>
          <div style={{ display: "flex", gap: 14, fontSize: 12, color: "var(--text-secondary)" }}>
            {showCompras && <Legend dot={COLORS.compras} label="Compras" />}
            {showVentas && <Legend dot={COLORS.ventas} label="Ventas" />}
          </div>
        </div>
        <div style={{ height: 320, position: "relative" }}>
          {loading && (
            <div style={{
              position: "absolute", inset: 0, background: "rgba(255,255,255,0.7)",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "var(--text-muted)", fontSize: 13, zIndex: 1,
            }}>Cargando...</div>
          )}
          <canvas ref={chartRef} />
        </div>
      </div>

      {/* Comparación anual: barras + tabla */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div style={{ ...CARD, padding: 20, minWidth: 0 }}>
          <div style={{ marginBottom: 14 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)", marginBottom: 2 }}>
              Comparación anual
            </h3>
            <p style={{ fontSize: 12, color: "var(--text-muted)" }}>Compras vs ventas acumuladas por año.</p>
          </div>
          <div style={{ height: 260, position: "relative" }}>
            {loading && (
              <div style={{
                position: "absolute", inset: 0, background: "rgba(255,255,255,0.7)",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "var(--text-muted)", fontSize: 13, zIndex: 1,
              }}>Cargando...</div>
            )}
            <canvas ref={yearChartRef} />
          </div>
        </div>

        <div style={{ ...CARD, minWidth: 0, overflow: "hidden" }}>
          <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border-light)" }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)", margin: 0 }}>Resumen por año</h3>
            <p style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>Acumulado anual con margen y variación.</p>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#fafbfc" }}>
                  {["Año", "Compras", "Ventas", "Margen", "YoY"].map((h, i) => (
                    <th key={h} style={{
                      padding: "11px 14px", textAlign: i === 0 ? "left" : "right",
                      fontSize: 11, fontWeight: 600, color: "var(--text-secondary)",
                      textTransform: "uppercase", letterSpacing: 0.3,
                      borderBottom: "1px solid #eef1f6",
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {yearlyAgg.length === 0 && (
                  <tr><td colSpan={5} style={{ padding: "32px 14px", textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>
                    Sin datos en el rango seleccionado.
                  </td></tr>
                )}
                {yearlyAgg.map((y, idx) => {
                  const dif = y.ventasNeto - y.comprasNeto;
                  const prev = yearlyAgg[idx - 1];
                  const yoy = prev && prev.ventasNeto > 0
                    ? ((y.ventasNeto - prev.ventasNeto) / prev.ventasNeto) * 100
                    : null;
                  return (
                    <tr key={y.year}>
                      <td style={{ padding: "11px 14px", fontSize: 13, color: "var(--text-primary)", fontWeight: 600, borderBottom: "1px solid var(--border-light)" }}>{y.year}</td>
                      <td style={{ padding: "11px 14px", fontSize: 13, color: "var(--text-secondary)", textAlign: "right", borderBottom: "1px solid var(--border-light)", fontVariantNumeric: "tabular-nums" }}>{fmtCLP(y.comprasNeto)}</td>
                      <td style={{ padding: "11px 14px", fontSize: 13, color: "var(--text-secondary)", textAlign: "right", borderBottom: "1px solid var(--border-light)", fontVariantNumeric: "tabular-nums" }}>{fmtCLP(y.ventasNeto)}</td>
                      <td style={{ padding: "11px 14px", fontSize: 13, fontWeight: 600, color: dif >= 0 ? "#059669" : "#dc2626", textAlign: "right", borderBottom: "1px solid var(--border-light)", fontVariantNumeric: "tabular-nums" }}>
                        {dif >= 0 ? "+" : ""}{fmtCLP(dif)}
                      </td>
                      <td style={{ padding: "11px 14px", textAlign: "right", borderBottom: "1px solid var(--border-light)" }}>
                        {yoy === null ? (
                          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>—</span>
                        ) : (
                          <span style={{
                            display: "inline-flex", alignItems: "center", gap: 3,
                            padding: "3px 8px", borderRadius: 999, fontSize: 11, fontWeight: 600,
                            background: yoy >= 0 ? "#ecfdf5" : "#fef2f2",
                            color: yoy >= 0 ? "#059669" : "#dc2626",
                          }}>
                            {yoy >= 0 ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                            {yoy >= 0 ? "+" : ""}{yoy.toFixed(1)}%
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function ToggleChip({ active, onClick, color, label }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "6px 12px", fontSize: 12, fontWeight: 600,
        border: `1.5px solid ${active ? color : "#e2e8f0"}`,
        borderRadius: 999, cursor: "pointer",
        background: active ? color + "15" : "#fff",
        color: active ? color : "#94a3b8",
        transition: "all 0.15s",
      }}
    >
      <span style={{ width: 8, height: 8, borderRadius: 999, background: active ? color : "#cbd5e1" }} />
      {label}
    </button>
  );
}

function YearStepper({ value, onChange, min, max }) {
  const dec = () => value > min && onChange(value - 1);
  const inc = () => value < max && onChange(value + 1);
  return (
    <div style={{
      display: "inline-flex", alignItems: "center",
      border: "1.5px solid var(--border)", borderRadius: 8, background: "var(--bg-card)", overflow: "hidden",
    }}>
      <button onClick={dec} disabled={value <= min} style={{
        padding: "6px 10px", border: "none", background: "transparent",
        cursor: value <= min ? "not-allowed" : "pointer",
        color: value <= min ? "#cbd5e1" : "#64748b", fontSize: 14, fontWeight: 600,
      }}>−</button>
      <span style={{ padding: "6px 12px", fontSize: 13, fontWeight: 600, color: "var(--text-primary)", minWidth: 50, textAlign: "center", borderLeft: "1px solid #e2e8f0", borderRight: "1px solid #e2e8f0" }}>
        {value}
      </span>
      <button onClick={inc} disabled={value >= max} style={{
        padding: "6px 10px", border: "none", background: "transparent",
        cursor: value >= max ? "not-allowed" : "pointer",
        color: value >= max ? "#cbd5e1" : "#64748b", fontSize: 14, fontWeight: 600,
      }}>+</button>
    </div>
  );
}

function Legend({ dot, label }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
      <span style={{ width: 8, height: 8, background: dot, borderRadius: 999 }} /> {label}
    </span>
  );
}
